// src/pages/RolePermissionCheck.js
import React, { useEffect, useState } from "react";
import FormLayout from "../layouts/FormLayout";
import LookupUmat from "../components/LookupUmat";
import { getUser, getJwt } from "../utils/auth";
import { toPinyinSync, capitalizePinyin } from '../utils/pinyinUtil';
import { hasUmatPermission } from "../utils/wkCommon";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

function computeDutyLevel(umatAttrs) {
  if (!umatAttrs) return 0;
  if (umatAttrs.dianchuanshi) return 7;
  if (umatAttrs.jiang_Shi) return 6;
  if (umatAttrs.jiangYuan) return 5;
  if (umatAttrs.tanzhu) return 4;
  if (umatAttrs.qingKou) return 3;
  if (umatAttrs.sd3Hari) return 2;
  return 1;
}

function getFirstUmatAttributes(json) {
  if (!json) return null;
  if (Array.isArray(json.data) && json.data.length > 0) {
    const first = json.data[0];
    return first.attributes ? { id: first.id, ...first.attributes } : first;
  }
  if (json.data && json.data.attributes) {
    return { id: json.data.id, ...json.data.attributes };
  }
  return json;
}

function resolveRelationAsArray(rel) {
  if (!rel) return [];
  if (Array.isArray(rel.data)) {
    return rel.data.map((item) => (item.attributes ? { id: item.id, ...item.attributes } : item));
  }
  if (rel.data && !Array.isArray(rel.data) && typeof rel.data === "object") {
    const it = rel.data;
    return [it.attributes ? { id: it.id, ...it.attributes } : it];
  }
  if (Array.isArray(rel)) {
    return rel.map((item) => (item.attributes ? { id: item.id, ...item.attributes } : item));
  }
  if (typeof rel === "object") {
    return [rel.attributes ? { id: rel.id, ...rel.attributes } : rel];
  }
  return [];
}

function dutyDescription(level) {
  switch (level) {
    case 7: return "7 — 點傳師 Dianchuanshi";
    case 6: return "6 — 講師 Jiang Shi";
    case 5: return "5 — 講員 Jiang Yuan";
    case 4: return "4 — 壇主 Tanzhu";
    case 3: return "3 — 清口人員 Kader QingKou";
    case 2: return "2 — 辦事 Petugas (Sudah Sidang Dharma 3 Hari)";
    case 1: return "1 — 道親 Umat";
    case 0:
    default: return "0 — Belum terdaftar";
  }
}

export default function RolePermissionCheck() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [umat, setUmat] = useState(null);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]); // resolved permissions for selected umat (dedup)
  const [allPermissions, setAllPermissions] = useState([]); // semua permission di sistem
  const [areas, setAreas] = useState([]); // area PIC
  const [regions, setRegions] = useState([]); // region PIC
  const [areaTerdaftar, setAreaTerdaftar] = useState(null); // area terdaftar umat
  const [ageGroups, setAgeGroups] = useState([]);
  const [dutyLevel, setDutyLevel] = useState(0);
  const [rawJson, setRawJson] = useState(null);
  const [selectedUmat, setSelectedUmat] = useState(null);

  const authUser = getUser();
  const strapiRoleName = authUser?.role?.name ?? "(tidak terdeteksi)";
  const jwt = getJwt();
  const headers = jwt ? { Authorization: `Bearer ${jwt}` } : {};

  // --- helper: fetch a role detail to populate umat_permissions if missing
  async function fetchRolePermissionsIfMissing(role) {
    if (role.umat_permissions && role.umat_permissions.length > 0) return role;
    try {
      ///api/umat-roles?filters[id][$eq]=1&populate=umat-permissions
      const url = `${API_URL}/api/umat-roles?filters%5Bid%5D%5B$eq%5D=${role.id}&populate=umat_permissions`;
      const resp = await fetch(url, { headers });
      if (!resp.ok) {
        console.warn(`Gagal fetch role ${role.id}: ${resp.status}`);
        return { ...role, umat_permissions: [] };
      }
      const json = await resp.json();
      const data = json?.data ?? json;
      const attrs = data?.attributes ? data.attributes : data;
      const permsRel = attrs?.umat_permissions ?? attrs?.permissions ?? null;
      const perms = resolveRelationAsArray(permsRel);
      return { ...role, umat_permissions: perms };
    } catch (e) {
      console.error("fetchRolePermissionsIfMissing error", e);
      return { ...role, umat_permissions: [] };
    }
  }

  // ambil semua permission di sistem (dipanggil sekali saat mount)
async function fetchAllPermissions() {
  try {
    const url = `${API_URL}/api/umat-permissions?pagination[limit]=1000`;
    const resp = await fetch(url, { headers });
    const json = await resp.json().catch(() => null);
    const arr = json?.data ?? json?.umat_permissions ?? [];
    const normalized = (Array.isArray(arr) ? arr : []).map((it) => (it?.attributes ? { id: it.id, ...it.attributes } : it));
    setAllPermissions(normalized);
    return normalized;
  } catch (e) {
    console.error("[RP] fetchAllPermissions error", e);
    setAllPermissions([]);
    return [];
  }
}

  // ambil umat by email dan resolve roles/permissions (inti dari file asal)
  async function fetchUmatByEmail(email) {
    if (!email) return;
    setLoading(true);
    setError(null);
    try {
      const filtersPart = `filters%5BLoginEmail%5D%5B$eq%5D=${encodeURIComponent(email)}`;
      const url = `${API_URL}/api/umats?${filtersPart}&populate%5Bumat_roles%5D%5Bpopulate%5D=umat_permissions`;
      const resp = await fetch(url, { headers });
      if (!resp.ok) throw new Error(`Fetch gagal: ${resp.status} ${resp.statusText}`);
      const json = await resp.json();
      setRawJson(json);

      const attrs = getFirstUmatAttributes(json);
      if (!attrs) {
        setError(`Tidak menemukan record Umat dengan LoginEmail=${email}`);
        setUmat(null);
        setDutyLevel(0);
        setRoles([]);
        setPermissions([]);
        return;
      }

      setUmat(attrs);

      // normalize roles (may not include permissions)
      const umatRolesList = resolveRelationAsArray(attrs.umat_roles);
  console.log(
    "fetchUmatByEmail attrs.umat_roles: ",
    attrs.umat_roles
  );
      const normalizedRoles = umatRolesList.map((r) => {
        const perms = resolveRelationAsArray(r.umat_permissions  ?? null);
        return { ...r, umat_permissions: perms };
      });

      // For roles without perms, fetch detail
      const completedRoles = await Promise.all(normalizedRoles.map(r => fetchRolePermissionsIfMissing(r)));
  console.log(
    "fetchUmatByEmail completedRoles: ",
    completedRoles
  );
      setRoles(completedRoles);

      // collect permissions dedup from roles AND direct umat_permissions
      const permMap = new Map();

      // direct umat_permissions
      const direct = resolveRelationAsArray(attrs.umat_permissions);
      direct.forEach((p) => {
        const key = p.id ?? p.name ?? JSON.stringify(p);
        permMap.set(key, p);
      });

      completedRoles.forEach((r) => {
        (r.umat_permissions || []).forEach((p) => {
          const key = p.id ?? p.name ?? JSON.stringify(p);
          permMap.set(key, p);
        });
      });

      const resolvedPerms = Array.from(permMap.values());
      setPermissions(resolvedPerms);

      // regions / areas / age group / duty level (sama seperti sebelumnya)
      const regionsList = resolveRelationAsArray(attrs.regions);
      setRegions(regionsList);

      const areasList = resolveRelationAsArray(attrs.areas);
      setAreas(areasList);

      const areaObj = attrs.area ? resolveRelationAsArray(attrs.area)[0] : null;
      setAreaTerdaftar(areaObj ?? null);

      const ageGroupList = resolveRelationAsArray(attrs.category_age_groups);
      setAgeGroups(ageGroupList);

      const level = computeDutyLevel(attrs);
      setDutyLevel(level);
    } catch (err) {
      console.error("RolePermissionCheck error:", err);
      setError(err.message || String(err));
      setRoles([]);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }

  // pada mount -> ambil semua permission sistem
  useEffect(() => {
    fetchAllPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // saat memilih umat dari Lookup
  useEffect(() => {
    if (selectedUmat && selectedUmat.LoginEmail) {
      // reset dulu biar UI jelas
      setUmat(null);
      setError(null);

      fetchUmatByEmail(selectedUmat.LoginEmail);
    } else {
      setUmat(null);
      setRawJson(null);
      setRoles([]);
      setPermissions([]);
      setAreas([]);
      setRegions([]);
      setAreaTerdaftar(null);
      setAgeGroups([]);
      setDutyLevel(0);
    }
  }, [selectedUmat]);

  // helper: cek apakah permission global dimiliki oleh umat (nama unik)
  function isPermissionOwnedByUmat(perm) {
    if (!perm || !permissions || permissions.length === 0) return false;
    // try compare by 'name' first, fallback to id
    const permName = perm.name ?? perm.title ?? perm.id;
  console.log(
    "isPermissionOwnedByUmat: ",
    permissions.map((p) => p.name + " " +  p.id)
  );
      return permissions.some((p) => {
      const pn = p.name ?? p.title ?? p.id;
      return pn && permName && pn === permName;
    });
  }

  return (
    <FormLayout title="Role & Permission Access Checker">
      <div style={{ maxWidth: 980, margin: "0 auto", padding: 20 }}>
        <style>{`
          .btn-submit { display: none !important; }
          .perm-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 8px; }
          .perm-item { padding: 8px 10px; border: 1px solid #ececec; border-radius: 6px; display:flex; align-items:center; justify-content:space-between; }
          .perm-label { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; padding-right:8px; }
          .has-yes { color: #137a13; font-weight:600; }
          .has-no { color: #777; }
        `}</style>

        <div style={{ marginBottom: 20 }}>
          <label style={{ fontWeight: "bold" }}>Pilih Umat</label>
          <LookupUmat
            value={selectedUmat}
            onChange={(v) => setSelectedUmat(v)}
            placeholder="Cari umat berdasarkan nama atau kode..."
          />
        </div>

        {loading && <div>Memuat data...</div>}
        {error && <div style={{ color: "crimson", marginBottom: 12 }}>Error: {error}</div>}

        {!loading && umat && (
          <>
            <h2>Profil Umat</h2>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                <tr><td style={{ padding: 6, fontWeight: "bold", width: 180 }}>Kode Umat</td><td style={{ padding: 6 }}>{umat.umatID ?? "-"}</td></tr>
                <tr><td style={{ padding: 6, fontWeight: "bold" }}>Chinese Name</td><td style={{ padding: 6 }}>{umat.chineseName ? `${umat.chineseName}${' '}${capitalizePinyin(toPinyinSync(umat.chineseName))}` : ""}</td></tr>
                <tr><td style={{ padding: 6, fontWeight: "bold" }}>Full Name</td><td style={{ padding: 6 }}>{umat.fullName ?? umat.displayName ?? "-"}</td></tr>
                <tr><td style={{ padding: 6, fontWeight: "bold" }}>Login Email</td><td style={{ padding: 6 }}>{umat.LoginEmail ?? "-"}</td></tr>
                <tr><td style={{ padding: 6, fontWeight: "bold" }}>Gender</td><td style={{ padding: 6 }}>{umat.gender ?? "-"}</td></tr>
                <tr><td>&nbsp;</td></tr>
                <tr><td style={{ padding: 6, fontWeight: "bold" }}>Area Terdaftar</td><td style={{ padding: 6 }}>{areaTerdaftar ? (areaTerdaftar.name ?? areaTerdaftar.code ?? areaTerdaftar.id) : "-"}</td></tr>
                <tr><td style={{ padding: 6, fontWeight: "bold" }}>Areas (PIC_Area)</td><td style={{ padding: 6 }}>{areas.length ? areas.map((a, i) => a.name ?? a.code ?? a.id ?? `area-${i}`).join("; ") : "-"}</td></tr>
                <tr><td style={{ padding: 6, fontWeight: "bold" }}>Regions (PIC_Region)</td><td style={{ padding: 6 }}>{regions.length ? regions.map((r, i) => r.name ?? r.code ?? r.id ?? `region-${i}`).join("; ") : "-"}</td></tr>
                <tr><td style={{ padding: 6, fontWeight: "bold" }}>Category / Age Groups</td><td style={{ padding: 6 }}>{ageGroups.length ? ageGroups.map((a, i) => a.name ?? a.id ?? `age-${i}`).join(", ") : "-"}</td></tr>
                <tr><td style={{ padding: 6, fontWeight: "bold" }}>Duty Level</td><td style={{ padding: 6 }}>{dutyDescription(dutyLevel)}</td></tr>
                <tr><td style={{ padding: 6, fontWeight: "bold" }}>Strapi Role</td><td style={{ padding: 6 }}>{strapiRoleName}</td></tr>
              </tbody>
            </table>

            <h3 style={{ marginTop: 20 }}>Roles</h3>
            {roles.length === 0 && <div>- Tidak ada role</div>}
            {roles.map((r, i) => (
              <div key={r.id ?? `role-${i}`} style={{ marginBottom: 8, padding: 8, border: "1px solid #eee" }}>
                <div style={{ fontWeight: "bold" }}>{r.name ?? r.title ?? "(unnamed role)"} <small>({r.slug ?? r.id})</small></div>
                <div style={{ marginTop: 6 }}>
                  Permissions:
                  {(r.umat_permissions && r.umat_permissions.length > 0)
                    ? <ul>{r.umat_permissions.map((p, j) => (<li key={p.id ?? p.name ?? `perm-${j}`}>{p.name ?? p.description ?? JSON.stringify(p)}</li>))}</ul>
                    : <div> - Tidak ada permission pada role ini</div>}
                </div>
              </div>
            ))}

            <h3 style={{ marginTop: 20 }}>All Resolved Permissions (Deduplicated)</h3>
            {permissions.length === 0 ? <div>- Tidak ada permission</div>
              : <ul>{permissions.map((p, i) => (<li key={p.id ?? p.name ?? `allperm-${i}`}>{p.name ?? p.description ?? JSON.stringify(p)}</li>))}</ul>}

            {/* ======= NEW: tampilkan semua permission sistem dan apakah dimiliki umat ======= */}
            <h3 style={{ marginTop: 22 }}>All System Permissions (showing which ones owned by selected umat)</h3>
            {allPermissions.length === 0 ? (
              <div>- Tidak menemukan permission di sistem. Pastikan koleksi `umat-permissions` ada.</div>
            ) : (
              <div className="perm-grid" style={{ marginTop: 8 }}>
                {allPermissions.map((perm) => {
                  const label = perm.name;
                  //const res = hasUmatPermission(umat.LoginEmail, perm.name);
                  const res = permissions.some(p => {
                    const pn = p.name ?? p.title ?? p.id;
                    return pn === perm.name;
                  });

                  return (
                    <div key={perm.id ?? label} className="perm-item" title={perm.description ?? ""}>
                      <div className="perm-label">{label}</div>
                      <div style={{ minWidth: 110, textAlign: "right" }}>
                      { (res) ? <span className="has-yes">✓ Owned</span> : <span className="has-no">— Not owned</span> }
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {/* ============================================================================ */}
          </>
        )}

        {!loading && !error && selectedUmat && !umat && (
          <div style={{ color: "#6b7280" }}>
            Data tidak ditemukan
          </div>
        )}
      </div>
    </FormLayout>
  );
}
